// ============================================================
// TMR Portal Admin — o botão "Exportar" que toda lista usa
// ============================================================
import { useEffect, useRef, useState } from 'react';
import { Btn } from './primitives';
import { Icons, StrokeIcon } from '../lib/icons';
import { useListExport } from '../lib/export/useListExport';
import type { ExportFormat, ExportSpec } from '../lib/export/types';

/** Props for {@link ExportMenu}. */
export interface ExportMenuProps<T> {
  /** What to export: the file's name, its columns, and how to read one page. */
  spec: ExportSpec<T>;
  /**
   * Rows the current filters match, from the list the operator is looking at. Zero disables the menu —
   * an empty spreadsheet is always a mistake, and finding that out after the download is worse.
   */
  totalCount: number;
  /** True while the list itself is still loading, so the menu does not offer to export nothing yet. */
  disabled?: boolean;
}

/**
 * Exports the list the operator is looking at, as XLSX or CSV (20 §9).
 *
 * One component for every list in the portal: the two formats, the progress while a big export walks its
 * pages, and the failure. What differs per list is the {@link ExportSpec} it is handed.
 *
 * It exports **what the screen is showing** — the spec's `fetchPage` carries the active filters — because
 * an operator who filtered to one gestor and got the whole base back would have a file nobody asked for,
 * and a wider one than they were allowed to look at in that moment.
 */
export function ExportMenu<T>({ spec, totalCount, disabled }: ExportMenuProps<T>) {
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const { state, progress, error, run, reset } = useListExport(spec);

  const running = state === 'running';
  const empty = totalCount === 0;

  // Closing on an outside click is what makes this behave like a menu rather than a panel that stays open
  // until something else is pressed.
  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const start = (format: ExportFormat) => {
    setOpen(false);
    void run(format);
  };

  const label = running
    ? progress && progress.total > 0
      // The count matters: a base of a few thousand takes seconds, and a button that only said
      // "Exportando…" would be indistinguishable from one that had frozen.
      ? `Exportando ${progress.loaded.toLocaleString('pt-BR')} de ${progress.total.toLocaleString('pt-BR')}…`
      : 'Exportando…'
    : 'Exportar';

  return (
    <div ref={container} style={{ position: 'relative', display: 'inline-flex' }}>
      <Btn
        variant="secondary"
        onClick={() => (state === 'error' ? reset() : setOpen((v) => !v))}
        disabled={running || disabled || empty}
        title={empty ? 'Não há nada para exportar com os filtros atuais.' : undefined}
      >
        <StrokeIcon d={Icons.download} size={15} /> {label}
      </Btn>

      {open && !running && (
        <div
          role="menu"
          className="pa-card"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            zIndex: 20,
            minWidth: 190,
            padding: 6,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            boxShadow: 'var(--shadow-md)',
          }}
        >
          <MenuItem onClick={() => start('xlsx')} primary="Excel (.xlsx)" secondary="Planilha formatada" />
          <MenuItem onClick={() => start('csv')} primary="CSV (.csv)" secondary="Texto separado por ponto e vírgula" />
        </div>
      )}

      {state === 'error' && error && (
        <div
          role="alert"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            zIndex: 20,
            maxWidth: 320,
            padding: '10px 12px',
            fontSize: 12.5,
            lineHeight: 1.45,
            background: 'oklch(96% 0.03 28)',
            border: '1px solid oklch(80% 0.09 28)',
            color: 'oklch(38% 0.12 28)',
            borderRadius: 'var(--radius-sm)',
          }}
        >
          {error.message}
        </div>
      )}
    </div>
  );
}

/** One row of the format menu. */
function MenuItem({ onClick, primary, secondary }: { onClick: () => void; primary: string; secondary: string }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="pa-menu-item"
      style={{
        display: 'block',
        width: '100%',
        padding: '7px 9px',
        textAlign: 'left',
        background: 'transparent',
        border: 0,
        borderRadius: 'var(--radius-xs)',
        cursor: 'pointer',
        fontFamily: 'var(--font-sans)',
      }}
    >
      <div style={{ fontSize: 13.5, fontWeight: 500 }}>{primary}</div>
      <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>{secondary}</div>
    </button>
  );
}
